import { Client, type Room } from '@colyseus/sdk';
import {
  NET_INPUT_INTERVAL_MS,
  NET_PROTOCOL_VERSION,
  NET_ROOM_NAME,
  type ChatMessage,
  type ChatScope,
  type EmoteMessage,
  type HeroId,
  type InputState,
  type LobbyCommand,
  type LobbyState,
  type MatchConfig,
  type NetInputMessage,
  type NetJoinOptions,
  type PackedSnapshot,
  SnapshotDecoder,
  type WelcomeMessage,
  type WorldSnapshot,
} from '../../shared/src/index.ts';

import { t, tr } from './i18n.ts';
export type NetStatus = 'idle' | 'connecting' | 'connected' | 'error' | 'closed';

export interface NetClientOptions {
  endpoint?: string;
  /** sesión de la cuenta (vacío = invitado): el servidor usa el nombre de la cuenta */
  token?: () => string;
  onStatus?: (status: NetStatus, detail?: string) => void;
  /** estado de la sala (jugadores, modo, fase); llega en cada cambio */
  onLobby?: (state: LobbyState) => void;
  onLobbyError?: (message: string) => void;
  /** empieza una partida (o entras a una en curso): trae la configuración y tu héroe */
  onWelcome?: (welcome: WelcomeMessage) => void;
  onSnapshot?: (snapshot: WorldSnapshot) => void;
  /** mensaje de chat (o historial al entrar en la sala) */
  onChat?: (messages: ChatMessage[]) => void;
  /** emoji de la rueda sobre un héroe */
  onEmote?: (message: EmoteMessage) => void;
}

/** Cliente de red: salas (crear / unirse por código / partida rápida), lobby y partida. */
export class NetClient {
  readonly endpoint: string;
  private readonly client: Client;
  options: NetClientOptions;
  private room?: Room;
  private seq = 0;
  private lastSentAt = 0;
  private closed = false;
  private readonly decoder = new SnapshotDecoder();
  private pingTimer = 0;
  /** ida y vuelta al servidor en ms (media suavizada); 0 = aún sin medir */
  ping = 0;

  constructor(options: NetClientOptions = {}) {
    this.options = options;
    this.endpoint = options.endpoint ?? defaultEndpoint();
    this.client = new Client(this.endpoint);
  }

  get sessionId(): string {
    return this.room?.sessionId ?? '';
  }

  get isConnected(): boolean {
    return Boolean(this.room) && !this.closed;
  }

  /** Crea una sala nueva (eres el anfitrión). */
  async create(name: string, heroId: HeroId, config?: MatchConfig): Promise<LobbyState> {
    return await this.attach(() => this.client.create(NET_ROOM_NAME, { ...this.joinOptions(name, heroId), config }));
  }

  /** Entra en la sala de ese código. */
  async joinByCode(code: string, name: string, heroId: HeroId): Promise<LobbyState> {
    return await this.attach(() => this.client.joinById(code.trim(), this.joinOptions(name, heroId)));
  }

  /** Entra en cualquier sala con hueco (o crea una). */
  async quickJoin(name: string, heroId: HeroId): Promise<LobbyState> {
    return await this.attach(() => this.client.joinOrCreate(NET_ROOM_NAME, this.joinOptions(name, heroId)));
  }

  private joinOptions(name: string, heroId: HeroId): NetJoinOptions {
    const token = this.options.token?.();
    return { protocol: NET_PROTOCOL_VERSION, name, heroId, ...(token ? { token } : {}) };
  }

  private async attach(join: () => Promise<Room>): Promise<LobbyState> {
    if (this.room) throw new Error(t('Ya estás en una sala'));
    this.options.onStatus?.('connecting');
    this.closed = false;
    try {
      this.room = await join();
    } catch (error) {
      const message = friendlyError(error);
      this.options.onStatus?.('error', message);
      throw new Error(message);
    }
    const room = this.room;

    const firstLobby = new Promise<LobbyState>((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error(t('El servidor no respondió'))), 5000);
      let resolved = false;
      room.onMessage('lobby', (state: LobbyState) => {
        if (!resolved) {
          resolved = true;
          window.clearTimeout(timeout);
          if (state.protocol !== NET_PROTOCOL_VERSION) {
            reject(new Error(t('Versión distinta: servidor {server}, cliente {client}. Recarga la página.', { server: state.protocol, client: NET_PROTOCOL_VERSION })));
            return;
          }
          this.options.onStatus?.('connected', state.roomId);
          resolve(state);
        }
        this.options.onLobby?.(state);
      });
    });

    room.onMessage('lobbyError', (message: { message: string }) => this.options.onLobbyError?.(message.message));
    room.onMessage('welcome', (welcome: WelcomeMessage) => {
      this.lastSentAt = 0;
      this.decoder.reset();
      this.options.onWelcome?.(welcome);
    });
    room.onMessage('snapshot', (snapshot: PackedSnapshot | WorldSnapshot) => this.options.onSnapshot?.(this.decoder.decode(snapshot)));
    room.onMessage('chat', (message: ChatMessage) => this.options.onChat?.([message]));
    room.onMessage('emote', (message: EmoteMessage) => this.options.onEmote?.(message));
    room.onMessage('chatHistory', (messages: ChatMessage[]) => {
      if (Array.isArray(messages)) this.options.onChat?.(messages);
    });
    room.onMessage('pong', (sentAt: number) => {
      const rtt = performance.now() - sentAt;
      if (!Number.isFinite(rtt) || rtt < 0 || rtt > 10000) return;
      // media móvil: suaviza los picos sin esconder un ping alto sostenido
      this.ping = this.ping === 0 ? rtt : this.ping * 0.75 + rtt * 0.25;
    });
    window.clearInterval(this.pingTimer);
    this.pingTimer = window.setInterval(() => this.room?.send('ping', performance.now()), 1000);
    room.send('ping', performance.now());
    room.onLeave((code, reason) => {
      this.room = undefined;
      window.clearInterval(this.pingTimer);
      this.ping = 0;
      if (this.closed) return;
      this.options.onStatus?.('closed', tr(reason) || t('código {code}', { code }));
    });
    room.onError((code, message) => this.options.onStatus?.('error', tr(message) || t('código {code}', { code })));

    room.send('hello', { protocol: NET_PROTOCOL_VERSION });
    try {
      return await firstLobby;
    } catch (error) {
      this.leave();
      throw error;
    }
  }

  sendLobby(command: LobbyCommand): void {
    this.room?.send('lobby', command);
  }

  sendChat(text: string, scope: ChatScope): void {
    this.room?.send('chat', { text, scope });
  }

  sendEmote(emote: number): void {
    this.room?.send('emote', { emote });
  }

  sendInput(input: InputState, force = false): void {
    if (!this.room) return;
    const now = performance.now();
    if (!force && now - this.lastSentAt < NET_INPUT_INTERVAL_MS) return;
    this.lastSentAt = now;
    const message: NetInputMessage = { seq: ++this.seq, input };
    this.room.send('input', message);
  }

  leave(): void {
    this.closed = true;
    this.room?.leave();
    this.room = undefined;
  }
}

function friendlyError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  // los errores del servidor llegan codificados (msg) y se traducen aquí
  if (message.includes('i18n:')) return tr(message);
  if (/ECONNREFUSED|Failed to fetch|NetworkError|network|connect/i.test(message)) {
    return t('No se pudo conectar con el servidor (¿está corriendo `npm run server`?)');
  }
  if (/locked|full/i.test(message)) return t('La sala está llena');
  if (/not found|invalid/i.test(message)) return t('No existe una sala con ese código');
  return message;
}

export function defaultEndpoint(): string {
  const override = new URLSearchParams(window.location.search).get('server');
  if (override) return override;
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const hostname = window.location.hostname === 'localhost' ? '127.0.0.1' : window.location.hostname;
  return `${protocol}://${hostname}:8787`;
}
